import T from '@/components/Helpers/Translate'
import { getFooterItems } from '@/lib/cms/getFooterItems'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useGTM } from '@/lib/hooks/google-analytics/useGTM'
import type { CMSError } from '@/lib/cms/types/CMSError'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import type { CosmicFooterItemType } from '@/lib/cms/cosmic/types/CosmicFooterItemsType'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { useFocusMode } from '@/lib/hooks/helpers/useFocusMode'

// * This is a temp solution until we fetch social media icons from Cosmic.
// * Normally this ESLint rule should be followed.
// eslint-disable-next-line import/no-mutable-exports
let socialIcons = [
  {
    alt: 'Facebook icon',
    href: 'https://www.facebook.com/UnicityNorthAmerica',
    src: '/icons/socialIcons/facebook.svg',
    className: 'social-icon-fb',
  },
  {
    alt: 'X icon',
    href: 'https://x.com/unicity',
    src: '/icons/socialIcons/x.svg',
    className: 'social-icon-x',
  },
  {
    alt: 'Pinterest icon',
    href: 'https://www.pinterest.com/unicityintl/pins',
    src: '/icons/socialIcons/pinterest.svg',
    className: 'social-icon-pinterest',
  },
  {
    alt: 'Instagram icon',
    href: 'https://www.instagram.com/unicityinternational',
    src: '/icons/socialIcons/instagram.svg',
    className: 'social-icon-instagram',
  },
  {
    alt: 'YouTube icon',
    href: 'https://www.youtube.com/channel/UCAiAqkbresXIqIQhT3Lfq7A',
    src: '/icons/socialIcons/youtube.svg',
    className: 'social-icon-youtube',
  },
]

export const MainFooter = () => {
  const { app } = useGlobalStore()
  const { gtm } = useGTM()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()

  const { isFocusMode } = useFocusMode()

  const currentYear = new Date().getFullYear()

  const [footerItems, setFooterItems] = useState<CosmicFooterItemType[]>([])
  const [footerError, setFooterError] = useState<CMSError | null>(null)
  const [isLoadingFooterItems, setIsLoadingFooterItems] = useState(false)

  const isRelativeUrl = (url: string) => url?.startsWith('/')

  const getFooterItemUrl = (url: string) => {
    if (isRelativeUrl(url)) {
      return parseLinkUrlWithLocale({ url })
    }

    return url
  }

  // TODO: Remove this ad hoc logic once we fetch social media icons from Cosmic
  const excludedSocialIconsCountries = new Set([
    'AT',
    'BE',
    'DE',
    'DK',
    'FR',
    'HU',
    'IT',
    'LU',
    'NO',
    'PL',
    'IE',
    'ES',
    'SE',
    'NL',
    'CZ',
  ])

  socialIcons = socialIcons.filter(({ className }) => {
    if (excludedSocialIconsCountries.has(app.alpha2)) {
      return (
        className !== 'social-icon-pinterest' && className !== 'social-icon-x'
      )
    }
    return true
  })

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    ;(async () => {
      setIsLoadingFooterItems(true)

      const footerItemsResult = await getFooterItems({
        locale: app.locale,
      })

      setIsLoadingFooterItems(false)

      if (!footerItemsResult) return
      if ('error' in footerItemsResult) {
        setFooterError(footerItemsResult.error)
        return
      }

      setFooterItems(footerItemsResult.items)
    })()
  }, [app.locale])

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleRenderFooterData = () => {
    if (isLoadingFooterItems) {
      return <div>loading...</div>
    }

    if (footerError) {
      return (
        <div>
          <ErrorAlert
            error={{
              error: {
                message: `${footerError.code} - ${footerError.message}`,
                code: 400,
              },
            }}
          />
        </div>
      )
    }

    return (
      <>
        {footerItems?.map(
          ({ sectionName, items, isShownFocusMode: isShownFocusModeSection }) =>
            // Only show if it isn't in focus mode or if it is in focus mode and isShownFocusModeSection is true
            ((isFocusMode && isShownFocusModeSection) || !isFocusMode) && (
              <div key={sectionName}>
                <p className="mb-4 text-xl font-medium text-primary">
                  {sectionName}
                </p>
                <div className="flex flex-col gap-3">
                  {items?.map(
                    ({ title, url, isShownFocusMode: isShownFocusModeItem }) =>
                      // Only show if it isn't in focus mode or if it is in focus mode and isShownFocusModeItem is true
                      ((isFocusMode && isShownFocusModeItem) ||
                        !isFocusMode) && (
                        <a
                          href={getFooterItemUrl(url)}
                          target={isRelativeUrl(url) ? '_self' : '_blank'}
                          rel={isRelativeUrl(url) ? '' : 'noreferrer'}
                          key={title}
                          className="text-sm text-[#0062A1] no-underline hover:underline"
                          onClick={(event) => {
                            event.preventDefault()

                            gtm.footerLinksClicked({
                              action: title,
                              category: sectionName,
                            })

                            mixpanelService
                              .trackEvent(MixpanelEvent.FOOTER_LINK_CLICKED, {
                                action: url.split('/').at(-1) || '',
                                category: sectionName,
                              })
                              .catch(() => {})

                            setTimeout(() => {
                              const newTab = window.open(
                                getFooterItemUrl(url),
                                '_blank',
                              )
                              if (newTab) {
                                newTab.opener = null
                              }
                            }, 250)
                          }}
                        >
                          {title}
                        </a>
                      ),
                  )}
                </div>
              </div>
            ),
        )}
      </>
    )
  }

  return (
    <>
      <div className="flex justify-center bg-[#82B1D8]/25">
        <div className="flex flex-col items-center gap-y-8 space-y-12 px-6 py-12 lg:flex-row lg:space-x-32 lg:py-16">
          <div className="flex h-full flex-col justify-end">
            <Image
              alt="Unicity logo"
              src="/unicity.svg"
              className="mx-auto lg:mx-0"
              width={160}
              height={25}
            />
            <div className="mt-11 flex gap-6">
              {socialIcons.map(({ alt, src, href, className }) => (
                <a
                  href={href}
                  key={alt}
                  className="flex h-[24px] cursor-pointer"
                  onClick={(event) => {
                    event.preventDefault()

                    gtm.footerLinksClicked({
                      action: alt,
                      category: 'social_platform',
                    })

                    mixpanelService
                      .trackEvent(MixpanelEvent.FOOTER_LINK_CLICKED, {
                        action: alt,
                        category: 'social_platform',
                      })
                      .catch(() => {})
                    setTimeout(() => {
                      const newTab = window.open(
                        getFooterItemUrl(href),
                        '_blank',
                      )
                      if (newTab) {
                        newTab.opener = null
                      }
                    }, 250)
                  }}
                >
                  <Image
                    alt={alt}
                    src={src}
                    width={24}
                    height={24}
                    className={`max-h-full hover:scale-125 ${className}`}
                  />
                </a>
              ))}
            </div>
          </div>
          <div className="!mt-0 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-4 md:justify-items-center">
            {handleRenderFooterData()}
          </div>
        </div>
      </div>
      <div className="flex h-16 items-center text-center">
        <p className="w-full text-xs text-[#2076AC]">
          © {currentYear} <T>copyright</T>
        </p>
      </div>
    </>
  )
}

export { socialIcons }
